import { isCustomerFeatureEnabled } from 'src/data/customer';
import { BE_CUSTOMER_FEATURES } from 'src/lib/featureFlags';

export const CHANNEL_TYPE = {
  social: 'social',
  native: 'native',
};

/**
 * Social channels
 */
export const SOCIAL_CHANNEL = {
  facebook: 'facebook',
  instagram: 'instagram',
  linkedin: 'linkedin',
  twitter: 'twitter',
  google: 'google',
  youtube: 'youtube',
  pinterest: 'pinterest',
};

/**
 * Social entity status
 */
export const SOCIAL_ENTITY_STATUS = {
  ok: 'ok',
  tokenInvalid: 'tokenInvalid',
  lostEntityAccess: 'lostEntityAccess',
  lostCustomerAccess: 'lostCustomerAccess',
};

const SOCIAL_ENTITY_STATUS_LABEL = {
  [SOCIAL_ENTITY_STATUS.ok]: 'OK',
  [SOCIAL_ENTITY_STATUS.tokenInvalid]: 'Expired token',
  [SOCIAL_ENTITY_STATUS.lostEntityAccess]: 'Missing permissions',
  [SOCIAL_ENTITY_STATUS.lostCustomerAccess]: 'Removed owner',
};

export const getSocialEntityStatusLabel = (status) => SOCIAL_ENTITY_STATUS_LABEL[status] || '';

/**
 * Native channels
 */
export const NATIVE_CHANNEL = {
  taboola: 'taboola',
  outbrain: 'outbrain',
  revcontent: 'revcontent',
  verizon: 'verizon',
  amazon: 'amazon',
  walmart: 'walmart',
  teads: 'teads',
};

/**
 * Reviews channels
 */
export const REVIEWS_CHANNEL = {
  trustpilot: 'trustpilot',
  amazon: 'amazon',
  g2: 'g2',
};

/**
 * Website channels
 */
export const WEBSITE_CHANNEL = {
  homepage: 'home_page',
  landingPages: 'landing_page',
};

/**
 * Channels combined enum
 */
export const CHANNEL = {
  ...SOCIAL_CHANNEL,
  ...NATIVE_CHANNEL,
  ...REVIEWS_CHANNEL,
  ...WEBSITE_CHANNEL,
};

const CHANNEL_PROPS = {
  [CHANNEL.facebook]: {
    label: 'Facebook',
    hint: 'Facebook and Instagram',
    performanceLabel: 'Meta Ads',
    performanceShortLabel: 'Meta',
    description:
      "Learn what copy worked and what didn't. Train Anyword to generate better performing copy based on your ads past performance.",
  },
  [CHANNEL.instagram]: {
    label: 'Instagram',
  },
  [CHANNEL.twitter]: {
    label: 'Twitter',
    hint: 'Twitter',
  },
  [CHANNEL.pinterest]: {
    label: 'Pinterest',
    hint: 'Pinterest',
  },
  [CHANNEL.linkedin]: {
    label: 'LinkedIn',
    hint: 'LinkedIn Text Ad',
    performanceLabel: 'LinkedIn Ads',
    performanceShortLabel: 'LinkedIn',
    description:
      "Learn what copy worked and what didn't. Train Anyword to generate better performing copy based on your ads past performance.",
  },
  [CHANNEL.google]: {
    label: 'Google',
    shortLabel: 'Google Ads',
    hint: 'Google Search Ads',
    performanceLabel: 'Google Ads',
    performanceShortLabel: 'Google',
    description:
      "Learn what copy worked and what didn't. Train Anyword to generate better performing copy based on your ads past performance.",
  },
  [CHANNEL.youtube]: {
    label: 'YouTube',
    hint: 'YouTube',
  },
  [CHANNEL.taboola]: {
    label: 'Taboola',
  },
  [CHANNEL.outbrain]: {
    label: 'Outbrain',
    hint: 'Outbrain and Taboola Native Ads',
  },
  [CHANNEL.revcontent]: {
    label: 'Revcontent',
  },
  [CHANNEL.teads]: {
    label: 'Teads',
  },
  [CHANNEL.verizon]: {
    label: 'Verizon Media',
    hint: 'Verizon Native Ads',
  },
  [CHANNEL.amazon]: {
    label: 'Amazon',
    hint: 'Amazon',
  },
  [CHANNEL.walmart]: {
    label: 'Walmart',
    hint: 'Walmart',
  },
  [CHANNEL.trustpilot]: {
    label: 'Trustpilot',
    performanceLabel: 'Trustpilot Reviews',
    performanceShortLabel: 'Trustpilot',
    description:
      'Make sure your messaging reflects the voice of your most satisfied customers by importing reviews and high-performing talking points.',
  },
  [CHANNEL.amazon]: {
    label: 'Amazon',
    performanceLabel: 'Amazon Reviews',
    performanceShortLabel: 'Amazon',
    description:
      'Make sure your messaging reflects the voice of your most satisfied customers by importing reviews and high-performing talking points.',
  },
  [CHANNEL.g2]: {
    label: 'G2',
    performanceLabel: 'G2 Reviews',
    performanceShortLabel: 'G2',
    description: '',
  },
  [CHANNEL.homepage]: {
    label: 'Homepage',
    performanceLabel: 'Homepage',
    performanceShortLabel: 'Homepage',
    description:
      'Get Anyword insights on your homepage copy. Leverage your high scoring talking points with new copy generations.',
  },
  [CHANNEL.landingPages]: {
    label: 'Landing Pages',
    performanceLabel: 'Landing Pages',
    performanceShortLabel: 'Landing Pages',
    description:
      'Get Anyword insights on your landing page copy. Leverage your high scoring talking points with new copy generations.',
  },
};

export const getChannelDescription = (channel) => {
  const props = CHANNEL_PROPS[channel];
  return (props && props.description) || '';
};

export const getChannelPerformanceLabel = (channel) => {
  const props = CHANNEL_PROPS[channel];
  return (props && props.performanceLabel) || '';
};

export const getChannelPerformanceShortLabel = (channel) => {
  const props = CHANNEL_PROPS[channel];
  return (props && props.performanceShortLabel) || '';
};

export const getChannelLabel = (channel) => {
  const props = CHANNEL_PROPS[channel];
  return (props && props.label) || '';
};

export const getChannelShortLabel = (channel) => {
  const props = CHANNEL_PROPS[channel];
  return (props && props.shortLabel) || getChannelLabel(channel) || '';
};

export const getChannelHint = (channel) => {
  const props = CHANNEL_PROPS[channel];
  return (props && props.hint) || '';
};

export function clampChannels(channels) {
  const prepared = Array.isArray(channels) ? channels : [];

  const socialValues = Object.values(SOCIAL_CHANNEL);
  const nativeValues = Object.values(NATIVE_CHANNEL);

  // Split to valid 'social' and 'native' values
  const filteredSocial = prepared.filter((channel) => socialValues.includes(channel));
  const filteredNative = prepared.filter((channel) => nativeValues.includes(channel));

  // 'social' values are preferred over 'native' ones
  return filteredSocial.length > 0 ? filteredSocial : filteredNative;
}

export function clampChannel(channel) {
  const values = Object.values(CHANNEL);

  if (values.includes(channel)) {
    return channel;
  }
  return SOCIAL_CHANNEL.facebook;
}

export function isSocialChannel(channel) {
  const socialValues = Object.values(SOCIAL_CHANNEL);
  return socialValues.includes(channel);
}

export function isNativeChannel(channel) {
  const nativeValues = Object.values(NATIVE_CHANNEL);
  return nativeValues.includes(channel);
}

export function inferChannelsType(channels) {
  if (!Array.isArray(channels) || channels.length === 0) {
    throw new Error('Empty or invalid input');
  }

  const onlySocial = channels.every(isSocialChannel);
  const onlyNative = channels.every(isNativeChannel);

  if (onlySocial) {
    return CHANNEL_TYPE.social;
  }
  if (onlyNative) {
    return CHANNEL_TYPE.native;
  }
  throw new Error('Cannot detect channel type');
}

export function channelSupportImportingVariations(channel, customer) {
  switch (channel) {
    case CHANNEL.facebook:
    case CHANNEL.linkedin:
      return true;
    case CHANNEL.google:
      return isCustomerFeatureEnabled(customer, BE_CUSTOMER_FEATURES.googleAdsCustomModeImport);
    case CHANNEL.twitter:
      return isCustomerFeatureEnabled(customer, BE_CUSTOMER_FEATURES.twitterAdsCustomModeImport);
    default:
      return false;
  }
}

export const alignChannelWithBackend = (channel) =>
  Object.values(REVIEWS_CHANNEL).includes(channel) ? `reviews_${channel}` : channel;
